import React, { lazy, Suspense } from 'react';
import './lib/submitLead'; // Capture the landing reference before route navigation.
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

import Layout from './components/Layout';
import HomePage from './pages/HomePage';
const BuyerIntentPage = lazy(() => import('./pages/services/BuyerIntentPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const ExhibitionAdsLandingPage = lazy(() => import('./pages/ExhibitionAdsLandingPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const FitOutReferencePage = lazy(() => import('./pages/FitOutReferencePage'));
const AIDesignStudioPage = lazy(() => import('./pages/AIDesignStudioPage'));
const ExhibitionStudioPage = lazy(() => import('./pages/ExhibitionStudioPage'));
const DesignResultPage = lazy(() => import('./pages/DesignResultPage'));
const EventStudioPage = lazy(() => import('./pages/EventStudioPage'));
const EventResultPage = lazy(() => import('./pages/EventResultPage'));
const InteriorStudioPage = lazy(() => import('./pages/InteriorStudioPage'));
const InteriorResultPage = lazy(() => import('./pages/InteriorResultPage'));
const EventsCalendarPage = lazy(() => import('./pages/EventsCalendarPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const InsightArticlePage = lazy(() => import('./pages/insights/ArticlePage'));
import { ApiKeyProvider } from './context/ApiKeyProvider';
const ROICalculatorPage = lazy(() => import('./pages/ROICalculatorPage'));
const CustomStandsPage = lazy(() => import('./pages/services/CustomStandsPage'));
const ModularSystemsPage = lazy(() => import('./pages/services/ModularSystemsPage'));
const TurnkeyServicesPage = lazy(() => import('./pages/services/TurnkeyServicesPage'));
const FabricationPage = lazy(() => import('./pages/services/FabricationPage'));
const FitOutPage = lazy(() => import('./pages/services/FitOutPage'));

// New Lead Magnet Pages
const ExhibitionGuidePage = lazy(() => import('./pages/resources/ExhibitionGuidePage'));
const CostCalculatorPage = lazy(() => import('./pages/resources/CostCalculatorPage'));
const ConsultationPage = lazy(() => import('./pages/ConsultationPage'));
const EventSetupDubaiPage = lazy(() => import('./pages/services/EventSetupDubaiPage'));
const FitOutDubaiPage = lazy(() => import('./pages/services/FitOutDubaiPage'));
const RestaurantFitOutDubaiPage = lazy(() => import('./pages/services/RestaurantFitOutDubaiPage'));
const ClinicFitOutDubaiPage = lazy(() => import('./pages/services/ClinicFitOutDubaiPage'));
const VillaRenovationDubaiPage = lazy(() => import('./pages/services/VillaRenovationDubaiPage'));
const ExhibitionStandsAbuDhabiPage = lazy(() => import('./pages/services/ExhibitionStandsAbuDhabiPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const App: React.FC = () => {
    const location = useLocation();

    return (
        <ApiKeyProvider>
            <Layout>
                <Suspense fallback={<div className="min-h-screen bg-fann-charcoal" />}>
                <AnimatePresence mode="wait">
                    <Routes location={location} key={location.pathname}>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/exhibition-stand-design-abu-dhabi" element={<BuyerIntentPage />} />
                        <Route path="/exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/medical-centre-fit-out-dubai" element={<BuyerIntentPage />} />
                        <Route path="/ar/exhibition-stands-dubai" element={<BuyerIntentPage />} />
                        <Route path="/gitex-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/big-5-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/gulfood-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/gulf-print-pack-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/idex-exhibition-stand-design-abu-dhabi" element={<BuyerIntentPage />} />
                        <Route path="/foodex-saudi-exhibition-stand-design-jeddah" element={<BuyerIntentPage />} />
                        <Route path="/cityscape-global-exhibition-stand-design-riyadh" element={<BuyerIntentPage />} />
                        <Route path="/saudi-build-exhibition-stand-design-riyadh" element={<BuyerIntentPage />} />
                        <Route path="/wetex-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/intersec-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/arab-health-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/arabian-travel-market-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/automechanika-dubai-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/adipec-exhibition-stand-design-abu-dhabi" element={<BuyerIntentPage />} />
                        <Route path="/adnec-exhibition-stand-design-abu-dhabi" element={<BuyerIntentPage />} />
                        <Route path="/dwtc-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/dec-expo-city-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/gisec-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/middle-east-energy-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/hotel-show-dubai-exhibition-stand-design" element={<BuyerIntentPage />} />
                        <Route path="/index-dubai-exhibition-stand-design" element={<BuyerIntentPage />} />
                        <Route path="/paperworld-middle-east-exhibition-stand-design" element={<BuyerIntentPage />} />
                        <Route path="/light-middle-east-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/beautyworld-middle-east-exhibition-stand-design" element={<BuyerIntentPage />} />
                        <Route path="/dubai-airshow-exhibition-stand-design" element={<BuyerIntentPage />} />
                        <Route path="/gulfhost-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/arabplast-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/airport-show-dubai-exhibition-stand-design" element={<BuyerIntentPage />} />
                        <Route path="/cabsat-exhibition-stand-design-dubai" element={<BuyerIntentPage />} />
                        <Route path="/seamless-middle-east-exhibition-stand-design" element={<BuyerIntentPage />} />
                        <Route path="/dubai-woodshow-exhibition-stand-design" element={<BuyerIntentPage />} />
                        <Route path="/gulfood-manufacturing-exhibition-stand-design" element={<BuyerIntentPage />} />
                        <Route path="/exhibition-stand-builders-dubai" element={<BuyerIntentPage />} />
                        <Route path="/fit-out-company-dubai" element={<BuyerIntentPage />} />
                        <Route path="/services" element={<ServicesPage />} />
                        {/* Service Detail Pages */}
                        <Route path="/services/custom-exhibition-stands-dubai" element={<CustomStandsPage />} />
                        <Route path="/services/modular-exhibition-systems-dubai" element={<ModularSystemsPage />} />
                        <Route path="/services/turnkey-exhibition-services-uae" element={<TurnkeyServicesPage />} />
                        <Route path="/services/exhibition-stand-fabrication-dubai" element={<FabricationPage />} />
                        <Route path="/services/interior-fitout-exhibition-spaces-dubai" element={<FitOutPage />} />
                        
                        <Route path="/event-setup-dubai" element={<EventSetupDubaiPage />} />
                        <Route path="/fit-out-dubai" element={<FitOutDubaiPage />} />
                        <Route path="/restaurant-fit-out-dubai" element={<RestaurantFitOutDubaiPage />} />
                        <Route path="/clinic-fit-out-dubai" element={<ClinicFitOutDubaiPage />} />
                        <Route path="/villa-renovation-dubai" element={<VillaRenovationDubaiPage />} />
                        <Route path="/exhibition-stands-abu-dhabi" element={<ExhibitionStandsAbuDhabiPage />} />
                        <Route path="/portfolio" element={<PortfolioPage />} />
                        <Route path="/exhibition-stand-quote" element={<ExhibitionAdsLandingPage />} />
                        <Route path="/portfolio/:slug" element={<ProjectDetailPage />} />
                        <Route path="/portfolio/fit-out/:referenceSlug" element={<FitOutReferencePage />} />
                        
                        <Route path="/fann-studio" element={<AIDesignStudioPage />} />
                        <Route path="/fann-studio/exhibition" element={<ExhibitionStudioPage />} />
                        <Route path="/fann-studio/exhibition/result" element={<DesignResultPage />} />
                        <Route path="/fann-studio/event" element={<EventStudioPage />} />
                        <Route path="/fann-studio/event/result" element={<EventResultPage />} />
                        <Route path="/fann-studio/interior" element={<InteriorStudioPage />} />
                        <Route path="/fann-studio/interior/result" element={<InteriorResultPage />} />
                        <Route path="/events-calendar" element={<EventsCalendarPage />} />
                        <Route path="/exhibitions/:showGuide" element={<EventsCalendarPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/insights" element={<InsightsPage />} />
                        <Route path="/insights/:slug" element={<InsightArticlePage />} />
                        <Route path="/ar/insights/:slug" element={<InsightArticlePage />} />
                        <Route path="/roi-calculator" element={<ROICalculatorPage />} />
                        
                        {/* Resources & Lead Magnets */}
                        <Route path="/resources/exhibition-guide" element={<ExhibitionGuidePage />} />
                        <Route path="/resources/cost-calculator" element={<CostCalculatorPage />} />
                        <Route path="/book-consultation" element={<ConsultationPage />} />
                        <Route path="/privacy-policy" element={<PrivacyPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                </AnimatePresence>
                </Suspense>
            </Layout>
        </ApiKeyProvider>
    );
};

export default App;
